import { useEffect, useState } from "react"
import { Combobox, CurrencyInput, EInputTypes, InputColumnTableProps, InputTable, InputTextCustom, formatCurrency, formatDate, getDate } from "medere-ui-framework"
import '../Budget.scss'
import { DisposablesBudgetDetailDTO, defaultDisposablesBudgetDetailDTO, getSubtotalValueDay, getSubtotalValueMonth } from "../../../dto/budget/BudgetDetailDTO";
import { InputTableContainerProps } from "../../../dto/InputTableContainerProps";
import { getDisposablesStockItemsForCombo, getStockItemPresentationValue } from "../../../servicesHooks/Budget/useBudgetService";
import { ComboDataDTO } from "../../../dto/ComboDataDTO";
import React from "react";
import { StockItemPresentationValueDTO } from "../../../dto/budget/StockItemPresentationValueDTO";
import { BudgetBodyComponent } from "./BudgetBodyComponent";

interface DisposablesTableProps extends InputTableContainerProps {
    items: DisposablesBudgetDetailDTO[];
}

export default function DisposablesTable({items, addToTotal, defaultEarnings, duration, CRUDBehaviour=true, showPriceByItem=true, showEarnings=true}: DisposablesTableProps) {

    const [subtotalValueDay, setSubtotalValueDay] = useState<number>(0);
    const [subtotalValueMonth, setSubtotalValueMonth] = useState<number>(0);
    const [newDisposablesBudgetDetail, setNewDisposablesBudgetDetail] = useState<DisposablesBudgetDetailDTO>(defaultDisposablesBudgetDetailDTO);
    const [editCostEnabled, setEditCostEnabled] = useState<boolean>(false);
    const [localEarnings, setLocalEarnings] = useState<number>(0);

    const calculateTotal = (unitsqty: number, unitprice: number, earning: number) => {
        return unitsqty * unitprice * (earning + 100) / 100;
    };

    function calculateItem(item: DisposablesBudgetDetailDTO): DisposablesBudgetDetailDTO {
        const totalMonth = calculateTotal(item.unitsqty, item.unitprice, item.earnings);
        return {
            ...item,
            valueday: duration ? ((duration > 0) ? (totalMonth / duration) : 0) : 0,
            valuemonth: totalMonth
        };
    }

    useEffect(() => {
        setNewDisposablesBudgetDetail(prev => ({...prev, valueday: duration ? ((duration > 0) ? prev.valuemonth / duration : 0) : 0}));
        let subtotal = 0;
        items.forEach((item) => {
            item.valuemonth = calculateTotal(item.unitsqty, item.unitprice, item.earnings);
            item.valueday = duration ? ((duration > 0) ? item.valuemonth / duration : 0) : 0;
            subtotal += item.valuemonth;
        });
        setSubtotalValueMonth(subtotal);
        setSubtotalValueDay(getSubtotalValueDay(items));
    }, [items, duration]);

    useEffect(() => {
        setLocalEarnings(defaultEarnings ?? 0);
        setNewDisposablesBudgetDetail(prev => ({...prev, earnings: defaultEarnings ?? 0}));
    }, [defaultEarnings]);

    useEffect(() => {
        if (newDisposablesBudgetDetail.item > 0)
            getStockItemPresentationValue(newDisposablesBudgetDetail.item).then((stockItemPresentation: StockItemPresentationValueDTO) => {
                setEditCostEnabled(!stockItemPresentation);
                setNewDisposablesBudgetDetail(prev => ({
                    ...prev,
                    unitprice: stockItemPresentation.price ?? 0,
                    pricesettingdate: stockItemPresentation.pricesettingdate ?? null
                }));
            });
    }, [newDisposablesBudgetDetail.item]);

    useEffect(() => {
        setNewDisposablesBudgetDetail(prev => ({
            ...prev,
            valuemonth: calculateTotal(prev.unitsqty, prev.unitprice, prev.earnings)
        }));
    }, [newDisposablesBudgetDetail.unitsqty, newDisposablesBudgetDetail.unitprice, newDisposablesBudgetDetail.earnings]);

    useEffect(() => {
        setNewDisposablesBudgetDetail(prev => ({...prev, valueday: duration ? ((duration > 0) ? prev.valuemonth / duration : 0) : 0}));
    }, [newDisposablesBudgetDetail.valuemonth, duration]);

    useEffect(() => {
        setSubtotalValueDay(duration ? ((duration > 0) ? subtotalValueMonth / duration : 0) : 0);
    }, [subtotalValueMonth, duration]);

    const columnsTable: InputColumnTableProps<DisposablesBudgetDetailDTO>[] = React.useMemo(() => [{
        body: (item) => <span className="nv-truncate nv-tip" data-pr-tooltip={item.description}>{item.description}</span>,
        className: "columnStart",
        header: "Item",
        inputClassName: "mx-1",
        input: (_item) =>
            <Combobox
                getItems={getDisposablesStockItemsForCombo}
                optionLabel="name"
                placeholder="Seleccionar"
                minPanelWidth={500}
                maxPanelWidth={600}
                setValue={(e: ComboDataDTO) => {
                    if (e) {
                        setNewDisposablesBudgetDetail(prev => ({
                            ...prev,
                            item: e.id ?? prev.item,
                            description: e.name ?? prev.description
                        }));
                    }
                }}
                value={newDisposablesBudgetDetail?.description}
            />,
        footerClassName: "font-bold",
        footer: "Subtotales",
    }, {
        className: "tableColumn justify-content-center",
        header: "Unidades",
        input: (_item) =>
            <InputTextCustom
                value={newDisposablesBudgetDetail.unitsqty}
                inputType={EInputTypes.NUMBER}
                inputClassName="text-center"
                onChange={(e: any) => {
                    setNewDisposablesBudgetDetail(prev => ({...prev, unitsqty: e.target.value ?? 0}));
                }}
            />,
        body: (item, index) =>
            <BudgetBodyComponent CRUDBehaviour={CRUDBehaviour} inputType={EInputTypes.NUMBER} value={item.unitsqty} inputClassName="text-center" onChange={(e: any) => {
                onHandleEditBudget(index, calculateItem({...item, unitsqty: e.target.value ?? 0}));
            }} />
    }, {
        className: "columnPrice",
        header:
            <div className="flex flex-column mr-2">
                <p>{"Valor unitario"}</p>
                {!editCostEnabled && newDisposablesBudgetDetail.pricesettingdate && <p className="text-xs">{`Última actualización ${formatDate(getDate(newDisposablesBudgetDetail.pricesettingdate))}`}</p>}
            </div>,
        input: (_item) =>
            <React.Fragment>
                {!editCostEnabled && <p className="mr-2" onClick={() => { setEditCostEnabled(true); }}>{formatCurrency(newDisposablesBudgetDetail.unitprice as number)}</p>}
                {editCostEnabled &&
                    <CurrencyInput ammount={newDisposablesBudgetDetail.unitprice} setAmmount={(newValue: number | null) => {
                        setNewDisposablesBudgetDetail(prev => ({...prev, unitprice: newValue ?? prev.unitprice ?? 0}));
                    }} />}
            </React.Fragment>,
        bodyClassName: "justify-content-end",
        body: (item, index) =>
            <BudgetBodyComponent CRUDBehaviour={CRUDBehaviour} currency value={item.unitprice} pClassName="mr-2" pValue={showPriceByItem ? formatCurrency(item.unitprice * (showEarnings ? 1 : (item.earnings + 100) / 100) as number) : ''} onChange={(newValue: number | null) => {
                onHandleEditBudget(index, calculateItem({...item, unitprice: newValue ?? 0}));
            }} />
    }, {
        className: "tableColumn justify-content-center",
        header: "% Ganancia",
        input: (_item) =>
            <InputTextCustom
                value={localEarnings}
                inputType={EInputTypes.NUMBER}
                inputClassName="text-center"
                onChange={(e: any) => {
                    setLocalEarnings(e.target.value ?? 0);
                    setNewDisposablesBudgetDetail(prev => ({...prev, earnings: e.target.value ?? 0}));
                }}
            />,
        body: (item, index) =>
            <BudgetBodyComponent CRUDBehaviour={CRUDBehaviour} inputType={EInputTypes.NUMBER} value={item.earnings} inputClassName="text-center" onChange={(e: any) => {
                onHandleEditBudget(index, calculateItem({...item, earnings: e.target.value ?? 0}));
            }} />,
        show: () => showEarnings
    }, {
        className: "columnPrice w-8",
        header: "Valor día",
        input: (_item) => <p>{formatCurrency(newDisposablesBudgetDetail.valueday as number)}</p>,
        bodyClassName: "font-bold justify-content-end",
        body: (item) => showPriceByItem ? formatCurrency(item.valueday as number) : '',
        footerClassName: "font-bold",
        footer: formatCurrency(subtotalValueDay as number),
        show: () => duration > 0
    }, {
        className: `columnPrice ${showEarnings ? 'w-10' : 'w-8'} pr-2`,
        header: "Valor total",
        input: (_item) => <p>{formatCurrency(newDisposablesBudgetDetail.valuemonth as number)}</p>,
        bodyClassName: "font-bold justify-content-end",
        body: (item) => showPriceByItem ? formatCurrency(item.valuemonth as number) : '',
        footerClassName: "font-bold",
        footer: formatCurrency(subtotalValueMonth as number)
    }] as InputColumnTableProps<DisposablesBudgetDetailDTO>[], [duration, newDisposablesBudgetDetail, subtotalValueDay, subtotalValueMonth, showPriceByItem, showEarnings, editCostEnabled]);

    function onHandleAddBudget(): void {
        if (newDisposablesBudgetDetail.item === -1) return;
        if (newDisposablesBudgetDetail.unitsqty === 0) return;
        items.push(newDisposablesBudgetDetail);
        const updatedValueMonth = getSubtotalValueMonth(items);
        if (addToTotal)
            addToTotal(updatedValueMonth - subtotalValueMonth);
        setSubtotalValueMonth(updatedValueMonth);
        setSubtotalValueDay(getSubtotalValueDay(items));
        setNewDisposablesBudgetDetail(defaultDisposablesBudgetDetailDTO);
    }

    function onHandleDeleteBudget(index: number): void {
        items.splice(index, 1);
        const updatedValueMonth = getSubtotalValueMonth(items);
        if (addToTotal)
            addToTotal(updatedValueMonth - subtotalValueMonth);
        setSubtotalValueMonth(updatedValueMonth);
        setSubtotalValueDay(getSubtotalValueDay(items));
    }

    function onHandleEditBudget(index: number, newItemValue: DisposablesBudgetDetailDTO): void {
        const valueMonthToAdd = newItemValue.valuemonth - items[index].valuemonth;
        items.splice(index, 1, newItemValue);
        const updatedValueMonth = subtotalValueMonth + valueMonthToAdd;
        setSubtotalValueDay(duration ? ((duration > 0) ? (updatedValueMonth / duration) : 0) : 0);
        setSubtotalValueMonth(updatedValueMonth);
        if (addToTotal) addToTotal(valueMonthToAdd);
    }

    return (
        <div className="nv-card budget-table-card">
            <span className="tableTitle"> Descartables </span>
            <InputTable divider dividerClassName="my-0 border-top-1" dividerInFirstItem columns={columnsTable} items={items} onAdd={CRUDBehaviour ? onHandleAddBudget : undefined} onDelete={CRUDBehaviour ? (_item, index) => onHandleDeleteBudget(index) : undefined} headerDivClassName="tableHeader align-items-center" inputDivClassName={"align-items-center"} footerDivClassName="tableFooter" divBtnClassName="max-w-54 min-w-54" />
        </div>
    );
}
